// pages/TermsOfServicePage.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { COLORS } from '../constants/colors';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 16px;
  color: #333;
`;

const LastUpdated = styled.p`
  color: #868e96;
  margin-bottom: 32px;
  font-style: italic;
`;

const Section = styled.section`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  margin-bottom: 16px;
  color: #495057;
  margin-top: 24px;
`;

const SubTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 12px;
  color: #495057;
  margin-top: 16px;
`;

const Text = styled.p`
  line-height: 1.8;
  color: #666;
  margin-bottom: 16px;
`;

const List = styled.ul`
  margin: 12px 0;
  padding-left: 24px;
`;

const ListItem = styled.li`
  line-height: 1.8;
  color: #666;
  margin-bottom: 8px;
`;

const EmailLink = styled.a`
  color: ${COLORS.PRIMARY};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const HighlightBox = styled.div`
  background: #fff3cd;
  border-left: 4px solid #ffc107;
  padding: 16px 20px;
  margin: 20px 0;
`;

export const TermsOfServicePage: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <Container>
      <Title>{t('terms.title')}</Title>
      <LastUpdated>{t('terms.lastUpdated')}: {new Date().toLocaleDateString()}</LastUpdated>

      <Section>
        <Text>{t('terms.introduction')}</Text>
      </Section>

      <Section>
        <SectionTitle>{t('terms.acceptance.title')}</SectionTitle>
        <Text>{t('terms.acceptance.description')}</Text>
      </Section>

      <Section>
        <SectionTitle>{t('terms.ageRequirement.title')}</SectionTitle>
        <Text>{t('terms.ageRequirement.description')}</Text>
        <HighlightBox>
          <Text style={{ marginBottom: 0 }}>{t('terms.ageRequirement.warning')}</Text>
        </HighlightBox>
      </Section>

      <Section>
        <SectionTitle>{t('terms.account.title')}</SectionTitle>
        
        <SubTitle>{t('terms.account.registration.title')}</SubTitle>
        <Text>{t('terms.account.registration.description')}</Text>
        <List>
          <ListItem>{t('terms.account.registration.items.accurate')}</ListItem>
          <ListItem>{t('terms.account.registration.items.current')}</ListItem>
          <ListItem>{t('terms.account.registration.items.complete')}</ListItem>
        </List>

        <SubTitle>{t('terms.account.security.title')}</SubTitle>
        <Text>{t('terms.account.security.description')}</Text>
        <List>
          <ListItem>{t('terms.account.security.items.confidential')}</ListItem>
          <ListItem>{t('terms.account.security.items.notify')}</ListItem>
          <ListItem>{t('terms.account.security.items.responsible')}</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>{t('terms.service.title')}</SectionTitle>
        <Text>{t('terms.service.description')}</Text>
        
        <SubTitle>{t('terms.service.features.title')}</SubTitle>
        <List>
          <ListItem>{t('terms.service.features.items.create')}</ListItem>
          <ListItem>{t('terms.service.features.items.customize')}</ListItem>
          <ListItem>{t('terms.service.features.items.save')}</ListItem>
          <ListItem>{t('terms.service.features.items.share')}</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>{t('terms.userContent.title')}</SectionTitle>
        
        <SubTitle>{t('terms.userContent.ownership.title')}</SubTitle>
        <Text>{t('terms.userContent.ownership.description')}</Text>

        <SubTitle>{t('terms.userContent.license.title')}</SubTitle>
        <Text>{t('terms.userContent.license.description')}</Text>

        <SubTitle>{t('terms.userContent.prohibited.title')}</SubTitle>
        <Text>{t('terms.userContent.prohibited.description')}</Text>
        <List>
          <ListItem>{t('terms.userContent.prohibited.items.illegal')}</ListItem>
          <ListItem>{t('terms.userContent.prohibited.items.harmful')}</ListItem>
          <ListItem>{t('terms.userContent.prohibited.items.infringing')}</ListItem>
          <ListItem>{t('terms.userContent.prohibited.items.offensive')}</ListItem>
          <ListItem>{t('terms.userContent.prohibited.items.spam')}</ListItem>
          <ListItem>{t('terms.userContent.prohibited.items.malicious')}</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>{t('terms.aiGenerated.title')}</SectionTitle>
        <Text>{t('terms.aiGenerated.description')}</Text>
        <List>
          <ListItem>{t('terms.aiGenerated.items.quality')}</ListItem>
          <ListItem>{t('terms.aiGenerated.items.appropriateness')}</ListItem>
          <ListItem>{t('terms.aiGenerated.items.uniqueness')}</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>{t('terms.intellectualProperty.title')}</SectionTitle>
        <Text>{t('terms.intellectualProperty.description')}</Text>
        <Text>{t('terms.intellectualProperty.restrictions')}</Text>
      </Section>

      <Section>
        <SectionTitle>{t('terms.prohibitedUse.title')}</SectionTitle>
        <Text>{t('terms.prohibitedUse.description')}</Text>
        <List>
          <ListItem>{t('terms.prohibitedUse.items.violate')}</ListItem>
          <ListItem>{t('terms.prohibitedUse.items.infringe')}</ListItem>
          <ListItem>{t('terms.prohibitedUse.items.harm')}</ListItem>
          <ListItem>{t('terms.prohibitedUse.items.impersonate')}</ListItem>
          <ListItem>{t('terms.prohibitedUse.items.interfere')}</ListItem>
          <ListItem>{t('terms.prohibitedUse.items.attempt')}</ListItem>
          <ListItem>{t('terms.prohibitedUse.items.scrape')}</ListItem>
          <ListItem>{t('terms.prohibitedUse.items.commercial')}</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>{t('terms.termination.title')}</SectionTitle>
        <Text>{t('terms.termination.description')}</Text>
        <Text>{t('terms.termination.userTermination')}</Text>
      </Section>

      <Section>
        <SectionTitle>{t('terms.disclaimer.title')}</SectionTitle>
        <HighlightBox>
          <Text style={{ marginBottom: 0 }}>{t('terms.disclaimer.description')}</Text>
        </HighlightBox>
      </Section>

      <Section>
        <SectionTitle>{t('terms.limitation.title')}</SectionTitle>
        <Text>{t('terms.limitation.description')}</Text>
      </Section>

      <Section>
        <SectionTitle>{t('terms.indemnification.title')}</SectionTitle>
        <Text>{t('terms.indemnification.description')}</Text>
      </Section>

      <Section>
        <SectionTitle>{t('terms.privacy.title')}</SectionTitle>
        <Text>
          {t('terms.privacy.description')} <EmailLink href="/privacy">{t('terms.privacy.link')}</EmailLink>
        </Text>
      </Section>

      <Section>
        <SectionTitle>{t('terms.changes.title')}</SectionTitle>
        <Text>{t('terms.changes.description')}</Text>
      </Section>

      <Section>
        <SectionTitle>{t('terms.governing.title')}</SectionTitle>
        <Text>{t('terms.governing.description')}</Text>
      </Section>

      <Section>
        <SectionTitle>{t('terms.severability.title')}</SectionTitle>
        <Text>{t('terms.severability.description')}</Text>
      </Section>

      <Section>
        <SectionTitle>{t('terms.contact.title')}</SectionTitle>
        <Text>{t('terms.contact.description')}</Text>
        <Text>
          <strong>Email:</strong> <EmailLink href="mailto:contact@storify-now.com">contact@storify-now.com</EmailLink><br />
          {/* <strong>Address:</strong> [Your Company Address] */}
        </Text>
      </Section>
    </Container>
  );
};